import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../api';
import PageHeader from '../components/PageHeader';
import { Avatar, Ic, Spinner, fmtNum } from '../components/ui';

const STATUS = {
  draft:     { label: 'Taslak',       cls: 'neutral', icon: 'save' },
  scheduled: { label: 'Zamanlandı',   cls: 'info',    icon: 'clock' },
  sending:   { label: 'Gönderiliyor', cls: 'warning', icon: 'send' },
  completed: { label: 'Tamamlandı',   cls: 'success', icon: 'check' },
};
const EMPTY_FORM = { title: '', message: '', parse_mode: 'HTML', scheduled_at: '', inline_buttons: [] };

const CSS = `
.bc-bots { display:grid; grid-template-columns:repeat(auto-fill, minmax(220px, 1fr)); gap:10px; }
.bc-bot { position:relative; display:flex; align-items:center; gap:12px; padding:12px 14px; border-radius:12px; border:1.5px solid var(--border3); background:var(--surface); cursor:pointer; text-align:left; color:var(--text); font:inherit; transition:border-color .12s, background .12s; min-width:0; }
.bc-bot:hover { background:var(--hover); }
.bc-bot.on { border-color:var(--accent); background:rgba(82,136,193,.12); }
.bc-bot .chk { position:absolute; top:8px; right:8px; width:20px; height:20px; border-radius:50%; border:2px solid var(--border2); display:flex; align-items:center; justify-content:center; color:#fff; transition:all .12s; }
.bc-bot.on .chk { background:var(--accent); border-color:var(--accent); }
.bc-grid { display:grid; grid-template-columns:minmax(0,1fr) 360px; gap:16px; align-items:start; }
.bc-side { position:sticky; top:16px; display:flex; flex-direction:column; gap:12px; }
.bc-seg { display:inline-flex; background:var(--surface3); border-radius:10px; padding:3px; gap:2px; }
.bc-seg button { border:none; background:none; color:var(--text3); font:inherit; font-size:13px; font-weight:600; padding:6px 12px; border-radius:8px; cursor:pointer; }
.bc-seg button.on { background:var(--surface); color:var(--link); box-shadow:var(--shadow-sm); }
.bc-tool { display:flex; gap:2px; padding:4px; border-bottom:1px solid var(--border3); }
.bc-tool button { border:none; background:none; color:var(--text2); width:32px; height:30px; border-radius:7px; cursor:pointer; font:inherit; font-size:13px; }
.bc-tool button:hover:not(:disabled) { background:var(--hover); color:var(--text); }
.bc-tool button:disabled { opacity:.35; cursor:default; }
.bc-editor { background:var(--surface3); border-radius:10px; border:1.5px solid transparent; transition:border-color .15s; }
.bc-editor:focus-within { border-color:var(--accent); }
.bc-editor textarea { width:100%; min-height:150px; background:none; border:none; outline:none; color:var(--text); font:inherit; font-size:14.5px; line-height:1.5; padding:10px 12px; resize:vertical; }
.bc-drop { border:1.5px dashed var(--border2); border-radius:10px; padding:16px; display:flex; align-items:center; justify-content:center; gap:10px; color:var(--text3); cursor:pointer; transition:all .15s; font-size:13.5px; }
.bc-drop:hover, .bc-drop.over { border-color:var(--accent); color:var(--link); background:rgba(82,136,193,.06); }
.bc-phone { background:#0e1621; border-radius:16px; overflow:hidden; border:1px solid var(--border3); }
.bc-hist { display:flex; flex-direction:column; gap:10px; }
.bc-item { display:flex; gap:12px; padding:14px; background:var(--surface); border:1px solid var(--border3); border-radius:12px; }
.bc-actionbar { display:flex; gap:10px; margin-top:6px; }
.bc-row-btn { display:flex; gap:8px; margin-bottom:8px; }
@media (max-width: 1100px) { .bc-grid { grid-template-columns:minmax(0,1fr) 300px; } }
@media (max-width: 900px) {
  .bc-grid { grid-template-columns:1fr; }
  .bc-side { position:static; }
}
@media (max-width: 768px) {
  .bc-bots { grid-template-columns:none; grid-auto-flow:column; grid-auto-columns:minmax(200px, 72%); overflow-x:auto; padding-bottom:4px; scrollbar-width:none; scroll-snap-type:x mandatory; }
  .bc-bots::-webkit-scrollbar { display:none; }
  .bc-bot { scroll-snap-align:start; }
  .bc-actionbar { position:sticky; bottom:0; background:var(--surface); padding:10px 0 calc(10px + env(safe-area-inset-bottom)); margin:6px -14px -14px; padding-left:14px; padding-right:14px; border-top:1px solid var(--border3); z-index:5; }
  .bc-row-btn { flex-wrap:wrap; }
  .bc-row-btn .input:first-child { flex:1 1 100% !important; }
}
`;

/* ── Telegram önizleme biçimlendirme ── */
function esc(s) { return String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }
function renderPreview(text, mode) {
  let h = esc(text);
  if (mode === 'HTML') {
    h = h.replace(/&lt;(\/?)(b|strong|i|em|u|ins|s|strike|del|code|pre|tg-spoiler)&gt;/gi, '<$1$2>')
      .replace(/&lt;a href=["'](https?:\/\/[^"'<>]+)["']&gt;/gi, '<a href="$1" target="_blank" rel="noreferrer">')
      .replace(/&lt;\/a&gt;/gi, '</a>');
  } else if (mode === 'Markdown') {
    h = h.replace(/\*([^*\n]+)\*/g, '<b>$1</b>')
      .replace(/_([^_\n]+)_/g, '<i>$1</i>')
      .replace(/`([^`\n]+)`/g, '<code>$1</code>')
      .replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer">$1</a>');
  }
  return h.replace(/\n/g, '<br/>');
}

export default function Broadcast() {
  const [bots, setBots] = useState([]);
  const [sel, setSel] = useState([]);
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [media, setMedia] = useState(null); // { file, url, type, name }
  const [dragOver, setDragOver] = useState(false);
  const [scheduleOn, setScheduleOn] = useState(false);
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState(null); // { ok, text }
  const [tab, setTab] = useState('compose');
  const fileRef = useRef();
  const taRef = useRef();

  useEffect(() => {
    api.get('/bots').then(r => {
      setBots(r.data || []);
      if (r.data?.length) setSel([r.data[0].id]);
    }).catch(() => {});
  }, []);

  const selKey = sel.join(',');
  useEffect(() => { if (sel.length) loadData(); else { setHistory([]); setStats(null); } }, [selKey]);

  async function loadData() {
    setLoading(true);
    try {
      const res = await Promise.all(sel.map(id => Promise.all([
        api.get(`/broadcasts/${id}`).then(r => r.data || []).catch(() => []),
        api.get(`/broadcasts/${id}/stats/overview`).then(r => r.data || {}).catch(() => ({})),
      ]).then(([h, s]) => ({ id, h, s }))));
      const name = id => bots.find(b => b.id === id)?.name || '';
      const allH = res.flatMap(x => x.h.map(b => ({ ...b, _botId: x.id, _botName: name(x.id) })))
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      const sum = k => res.reduce((t, x) => t + (Number(x.s[k]) || 0), 0);
      setHistory(allH);
      setStats({ total: sum('total'), total_sent: sum('total_sent'), total_failed: sum('total_failed'), sending: sum('sending'), drafts: sum('drafts') });
    } catch (e) { /* noop */ }
    setLoading(false);
  }

  function toggleBot(id) { setSel(s => (s.includes(id) ? s.filter(x => x !== id) : [...s, id])); }
  const selBots = bots.filter(b => sel.includes(b.id));
  const recipients = selBots.reduce((t, b) => t + (Number(b.user_count) || 0), 0);

  function pickFile(file) {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setMedia({ file, url: reader.result, type: file.type, name: file.name });
    reader.readAsDataURL(file);
  }

  function wrapSel(open, close) {
    const ta = taRef.current;
    if (!ta) return;
    const { selectionStart: a, selectionEnd: b, value } = ta;
    const inner = value.slice(a, b) || 'metin';
    const next = value.slice(0, a) + open + inner + close + value.slice(b);
    setForm(f => ({ ...f, message: next }));
    setTimeout(() => { ta.focus(); ta.setSelectionRange(a + open.length, a + open.length + inner.length); }, 0);
  }
  function fmt(kind) {
    const m = form.parse_mode;
    const map = m === 'HTML'
      ? { b: ['<b>', '</b>'], i: ['<i>', '</i>'], u: ['<u>', '</u>'], s: ['<s>', '</s>'], code: ['<code>', '</code>'], link: ['<a href="https://">', '</a>'] }
      : { b: ['*', '*'], i: ['_', '_'], code: ['`', '`'], link: ['[', '](https://)'] };
    if (map[kind]) wrapSel(...map[kind]);
  }

  const setBtn = (i, k, v) => setForm(f => ({ ...f, inline_buttons: f.inline_buttons.map((b, j) => (j === i ? { ...b, [k]: v } : b)) }));
  const addBtn = () => setForm(f => ({ ...f, inline_buttons: [...f.inline_buttons, { text: '', url: '' }] }));
  const rmBtn = i => setForm(f => ({ ...f, inline_buttons: f.inline_buttons.filter((_, j) => j !== i) }));

  async function send(isDraft = false) {
    if (!sel.length) return flash(false, 'En az bir bot seçin');
    if (!form.message.trim() && !media) return flash(false, 'Mesaj ya da medya gerekli');
    if (!isDraft && !scheduleOn && !window.confirm(`${selBots.length} bot üzerinden ${fmtNum(recipients)} kişiye gönderilsin mi?`)) return;
    setSending(true); setMsg(null);
    const fails = [];
    for (const b of selBots) {
      try {
        const fd = new FormData();
        fd.append('bot_id', b.id);
        fd.append('title', form.title || 'Duyuru');
        fd.append('message', form.message);
        fd.append('parse_mode', form.parse_mode);
        fd.append('inline_buttons', JSON.stringify(form.inline_buttons.filter(x => x.text && x.url)));
        fd.append('target_filter', JSON.stringify({}));
        if (scheduleOn && form.scheduled_at) fd.append('scheduled_at', form.scheduled_at);
        if (media?.file) fd.append('media', media.file);
        const r = await fetch(`/api${isDraft ? '/broadcasts/draft' : '/broadcasts'}`, {
          method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }, body: fd,
        });
        if (!r.ok) { let e = r.statusText; try { e = (await r.json()).error || e; } catch (x) { /* noop */ } fails.push(`${b.name}: ${e}`); }
      } catch (e) { fails.push(`${b.name}: ${e.message}`); }
    }
    setSending(false);
    if (fails.length) flash(false, 'Bazı botlarda hata: ' + fails.join(' · '));
    else {
      flash(true, isDraft ? 'Taslak kaydedildi' : (scheduleOn && form.scheduled_at) ? 'Gönderim zamanlandı' : 'Gönderim başladı');
      setForm(EMPTY_FORM); setMedia(null); setScheduleOn(false);
    }
    loadData();
  }
  function flash(ok, text) { setMsg({ ok, text }); setTimeout(() => setMsg(null), 5000); }

  async function del(b) {
    if (!window.confirm('Bu gönderim silinsin mi?')) return;
    try { await api.delete(`/broadcasts/${b.id}`); loadData(); } catch (e) { flash(false, 'Silinemedi'); }
  }
  function loadDraft(b) {
    setForm({ title: b.title || '', message: b.message || '', parse_mode: b.parse_mode ?? 'HTML', scheduled_at: '', inline_buttons: b.inline_buttons || [] });
    if (b._botId) setSel([b._botId]);
    setTab('compose');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  const preview = useMemo(() => renderPreview(form.message, form.parse_mode), [form.message, form.parse_mode]);
  const limit = media ? 1024 : 4096;
  const over = form.message.length > limit;
  const firstBot = selBots[0];
  const sendLabel = sending ? 'Gönderiliyor…' : (scheduleOn && form.scheduled_at) ? 'Zamanla' : `${fmtNum(recipients)} kişiye gönder`;

  return (
    <div className="page-enter">
      <style>{CSS}</style>
      <PageHeader title="Toplu Mesaj" subtitle="Bir veya birden fazla botun kullanıcılarına duyuru gönder" />

      {/* Botlar */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="flex-between" style={{ marginBottom: 12, gap: 10, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 15 }}>Alıcı botlar</div>
            <div className="text-muted text-sm">{sel.length ? `${sel.length} bot seçili · ${fmtNum(recipients)} alıcı` : 'Gönderim için bot seçin'}</div>
          </div>
          <div className="flex gap-6">
            <button className="btn btn-sm btn-ghost" onClick={() => setSel(bots.map(b => b.id))} disabled={!bots.length || sel.length === bots.length}>Tümünü seç</button>
            <button className="btn btn-sm btn-ghost" onClick={() => setSel([])} disabled={!sel.length}>Temizle</button>
          </div>
        </div>
        {!bots.length ? <div className="text-muted text-sm">Henüz bot yok</div> : (
          <div className="bc-bots">
            {bots.map(b => {
              const on = sel.includes(b.id);
              return (
                <button key={b.id} className={'bc-bot' + (on ? ' on' : '')} onClick={() => toggleBot(b.id)}>
                  <Avatar id={b.id} name={b.name} size={44}>{b.bot_type === 'date' ? <Ic n="heart" s={22} /> : null}</Avatar>
                  <div style={{ minWidth: 0, flex: 1, paddingRight: 18 }}>
                    <div style={{ fontWeight: 600, fontSize: 14.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.name}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--text3)', marginTop: 1, whiteSpace: 'nowrap' }}>
                      <Ic n="users" s={14} />{fmtNum(b.user_count)} kullanıcı
                    </div>
                  </div>
                  <span className="chk">{on && <Ic n="check" s={12} sw={3} />}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* İstatistikler */}
      {stats && (
        <div className="stats-grid">
          {[
            { label: 'Toplam gönderim', value: stats.total, color: 'var(--text)' },
            { label: 'Ulaşılan kişi', value: stats.total_sent, color: 'var(--success)' },
            { label: 'Başarısız', value: stats.total_failed, color: 'var(--danger)' },
            { label: 'Zamanlanmış', value: stats.sending, color: 'var(--warning)' },
            { label: 'Taslak', value: stats.drafts, color: 'var(--text2)' },
          ].map(s => (
            <div key={s.label} className="stat-card">
              <div className="stat-value" style={{ color: s.color }}>{fmtNum(s.value)}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      <div className="tabs" style={{ marginBottom: 16 }}>
        <button className={'tab' + (tab === 'compose' ? ' active' : '')} onClick={() => setTab('compose')}>Yeni mesaj</button>
        <button className={'tab' + (tab === 'history' ? ' active' : '')} onClick={() => setTab('history')}>Geçmiş{history.length ? ` (${history.length})` : ''}</button>
      </div>

      {tab === 'compose' && (
        <div className="bc-grid">
          <div className="card">
            {msg && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, marginBottom: 14, background: msg.ok ? 'var(--success-s)' : 'var(--danger-s)', color: msg.ok ? 'var(--success)' : 'var(--danger)', fontSize: 14 }}>
                <Ic n={msg.ok ? 'checkCircle' : 'info'} s={20} />{msg.text}
              </div>
            )}

            <div className="input-group">
              <label>Başlık (sadece sende görünür)</label>
              <input className="input" placeholder="Örn. Nisan kampanyası" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
            </div>

            <div className="input-group">
              <div className="flex-between" style={{ marginBottom: 5, gap: 8, flexWrap: 'wrap' }}>
                <label style={{ margin: 0 }}>Mesaj</label>
                <div className="bc-seg">
                  {[['HTML', 'HTML'], ['Markdown', 'Markdown'], ['', 'Düz metin']].map(([v, l]) => (
                    <button key={l} className={form.parse_mode === v ? 'on' : ''} onClick={() => setForm({ ...form, parse_mode: v })}>{l}</button>
                  ))}
                </div>
              </div>
              <div className="bc-editor">
                <div className="bc-tool">
                  <button title="Kalın" disabled={!form.parse_mode} onClick={() => fmt('b')} style={{ fontWeight: 800 }}>B</button>
                  <button title="İtalik" disabled={!form.parse_mode} onClick={() => fmt('i')} style={{ fontStyle: 'italic' }}>I</button>
                  <button title="Altı çizili" disabled={form.parse_mode !== 'HTML'} onClick={() => fmt('u')} style={{ textDecoration: 'underline' }}>U</button>
                  <button title="Üstü çizili" disabled={form.parse_mode !== 'HTML'} onClick={() => fmt('s')} style={{ textDecoration: 'line-through' }}>S</button>
                  <button title="Kod" disabled={!form.parse_mode} onClick={() => fmt('code')} style={{ fontFamily: 'monospace' }}>{'</>'}</button>
                  <button title="Bağlantı" disabled={!form.parse_mode} onClick={() => fmt('link')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Ic n="link" s={16} /></button>
                </div>
                <textarea ref={taRef} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} placeholder="Kullanıcılara gidecek mesajı yazın…" />
              </div>
              <div style={{ fontSize: 12, color: over ? 'var(--danger)' : 'var(--text3)', textAlign: 'right', marginTop: 4 }}>{form.message.length} / {limit}{media ? ' (medya açıklaması)' : ''}</div>
            </div>

            <div className="input-group">
              <label>Medya</label>
              {media ? (
                <div style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', background: 'var(--surface3)' }}>
                  {media.type.startsWith('image/') ? <img src={media.url} alt="" style={{ width: '100%', maxHeight: 220, objectFit: 'cover', display: 'block' }} />
                    : media.type.startsWith('video/') ? <video src={media.url} controls style={{ width: '100%', maxHeight: 240, display: 'block', background: '#000' }} />
                      : <div style={{ padding: 14, display: 'flex', gap: 10, alignItems: 'center' }}><Ic n="file" />{media.name}</div>}
                  <button onClick={() => setMedia(null)} title="Kaldır" style={{ position: 'absolute', top: 8, right: 8, width: 30, height: 30, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,.6)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Ic n="close" s={16} /></button>
                </div>
              ) : (
                <div className={'bc-drop' + (dragOver ? ' over' : '')} onClick={() => fileRef.current?.click()}
                  onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)}
                  onDrop={e => { e.preventDefault(); setDragOver(false); pickFile(e.dataTransfer.files?.[0]); }}>
                  <Ic n="image" />Resim, GIF veya video ekle — sürükleyip bırakabilirsin
                </div>
              )}
              <input ref={fileRef} type="file" accept="image/*,video/*" style={{ display: 'none' }} onChange={e => { pickFile(e.target.files?.[0]); e.target.value = ''; }} />
            </div>

            <div className="input-group">
              <label>Butonlar</label>
              {form.inline_buttons.map((b, i) => (
                <div key={i} className="bc-row-btn">
                  <input className="input" value={b.text} onChange={e => setBtn(i, 'text', e.target.value)} placeholder="Buton yazısı" style={{ flex: 1 }} />
                  <input className="input" value={b.url} onChange={e => setBtn(i, 'url', e.target.value)} placeholder="https://…" style={{ flex: 2 }} />
                  <button className="btn btn-danger btn-icon" onClick={() => rmBtn(i)} title="Kaldır"><Ic n="trash" s={18} /></button>
                </div>
              ))}
              <button className="btn btn-sm btn-teal" onClick={addBtn}><Ic n="plus" s={16} />Buton ekle</button>
            </div>

            <div className="input-group" style={{ marginBottom: 8 }}>
              <label className="flex-center gap-10" style={{ cursor: 'pointer', color: 'var(--text)', fontSize: 14, fontWeight: 500 }}>
                <span className="toggle"><input type="checkbox" checked={scheduleOn} onChange={e => setScheduleOn(e.target.checked)} /><span className="toggle-slider" /></span>
                İleri bir tarihe zamanla
              </label>
              {scheduleOn && (
                <input type="datetime-local" className="input" style={{ marginTop: 8 }} value={form.scheduled_at} onChange={e => setForm({ ...form, scheduled_at: e.target.value })} />
              )}
            </div>

            <div className="bc-actionbar">
              <button className="btn btn-ghost" onClick={() => send(true)} disabled={sending || !sel.length} style={{ flex: 1 }}><Ic n="save" s={18} />Taslak</button>
              <button className="btn btn-primary" onClick={() => send(false)} disabled={sending || !sel.length || over || (!form.message.trim() && !media) || (scheduleOn && !form.scheduled_at)} style={{ flex: 2 }}>
                <Ic n={scheduleOn ? 'clock' : 'send'} s={18} />{sendLabel}
              </button>
            </div>
          </div>

          <div className="bc-side">
            <div className="bc-phone">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'var(--surface)', borderBottom: '1px solid #0a1119' }}>
                <Avatar id={firstBot?.id} name={firstBot?.name || 'Bot'} size={36} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{firstBot?.name || 'Bot seçin'}{selBots.length > 1 ? ` +${selBots.length - 1}` : ''}</div>
                  <div style={{ fontSize: 12.5, color: 'var(--text3)' }}>bot</div>
                </div>
              </div>
              <div style={{ padding: '18px 14px 16px', minHeight: 220, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                <div style={{ maxWidth: '92%' }}>
                  <div style={{ background: '#182533', borderRadius: '14px 14px 14px 4px', overflow: 'hidden' }}>
                    {media && media.type.startsWith('image/') && <img src={media.url} alt="" style={{ width: '100%', maxHeight: 200, objectFit: 'cover', display: 'block' }} />}
                    {media && media.type.startsWith('video/') && <div style={{ height: 140, background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}><Ic n="send" s={30} style={{ transform: 'rotate(0deg)' }} /></div>}
                    <div style={{ padding: '7px 10px 6px' }}>
                      {form.message
                        ? <div className="bc-prev" style={{ fontSize: 14.5, lineHeight: 1.42, wordBreak: 'break-word' }} dangerouslySetInnerHTML={{ __html: preview }} />
                        : <div style={{ fontSize: 14, color: 'var(--text3)', fontStyle: 'italic' }}>Mesaj önizlemesi</div>}
                      <div style={{ textAlign: 'right', fontSize: 11.5, color: '#6d7f8f', marginTop: 2 }}>{new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</div>
                    </div>
                  </div>
                  {form.inline_buttons.filter(b => b.text).map((b, i) => (
                    <div key={i} style={{ marginTop: 3, textAlign: 'center', padding: '8px 12px', borderRadius: 10, background: 'rgba(24,37,51,.75)', color: '#fff', fontSize: 14, fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                      {b.text}{b.url && <Ic n="external" s={13} c="#aab6c2" />}
                    </div>
                  ))}
                </div>
              </div>
              <style>{'.bc-prev a { color:#6ab3f3; } .bc-prev code, .bc-prev pre { background:rgba(0,0,0,.25); color:#8ccfff; border-radius:4px; padding:0 4px; font-family:monospace; } .bc-prev tg-spoiler { background:#47596b; color:transparent; border-radius:4px; }'}</style>
            </div>

            <div className="card" style={{ padding: 14 }}>
              <div className="flex-between" style={{ marginBottom: selBots.length ? 10 : 0 }}>
                <span className="text-muted text-sm">Toplam alıcı</span>
                <span style={{ fontSize: 20, fontWeight: 700 }}>{fmtNum(recipients)}</span>
              </div>
              {selBots.map(b => (
                <div key={b.id} className="flex-between" style={{ padding: '6px 0', borderTop: '1px solid var(--border3)', fontSize: 13.5 }}>
                  <span className="flex-center gap-8" style={{ minWidth: 0 }}><Avatar id={b.id} name={b.name} size={22} /><span className="truncate">{b.name}</span></span>
                  <span className="text-muted">{fmtNum(b.user_count)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'history' && (
        loading ? <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Spinner /></div>
          : !history.length ? (
            <div className="card empty-state"><div className="icon"><Ic n="megaphone" s={44} /></div><p>Henüz gönderim yok</p></div>
          ) : (
            <div className="bc-hist">
              {history.map(b => {
                const st = STATUS[b.is_draft ? 'draft' : b.status] || { label: b.status, cls: 'neutral', icon: 'info' };
                const sent = Number(b.sent_count) || 0, failed = Number(b.failed_count) || 0;
                const pct = sent + failed ? Math.round(sent / (sent + failed) * 100) : 0;
                return (
                  <div key={`${b._botId}-${b.id}`} className="bc-item">
                    <div style={{ width: 42, height: 42, borderRadius: '50%', background: `var(--${st.cls === 'neutral' ? 'surface3' : st.cls + '-s'})`, color: st.cls === 'neutral' ? 'var(--text2)' : `var(--${st.cls})`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Ic n={st.icon} s={20} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="flex-between" style={{ gap: 8, alignItems: 'flex-start' }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 14.5 }} className="truncate">{b.title || 'Duyuru'}</div>
                          <div className="text-muted text-sm flex-center gap-6" style={{ flexWrap: 'wrap' }}>
                            {sel.length > 1 && <span className="badge badge-accent">{b._botName}</span>}
                            <span>{b.scheduled_at ? '⏰ ' + new Date(b.scheduled_at).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' }) : new Date(b.created_at).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' })}</span>
                          </div>
                        </div>
                        <span className={`badge badge-${st.cls}`}>{st.label}</span>
                      </div>
                      <div style={{ fontSize: 13.5, color: 'var(--text2)', margin: '6px 0 8px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {b.media_path && <Ic n="image" s={14} style={{ display: 'inline-block', verticalAlign: '-2px', marginRight: 5 }} />}{(b.message || '').replace(/<[^>]+>/g, '')}
                      </div>
                      {!b.is_draft && (sent + failed > 0) && (
                        <div style={{ marginBottom: 8 }}>
                          <div className="progress-bar"><div className="progress-fill green" style={{ width: pct + '%' }} /></div>
                          <div className="text-xs text-muted" style={{ marginTop: 4 }}><span style={{ color: 'var(--success)' }}>{fmtNum(sent)} ulaştı</span>{failed > 0 && <> · <span style={{ color: 'var(--danger)' }}>{fmtNum(failed)} başarısız</span></>}</div>
                        </div>
                      )}
                      <div className="flex gap-6">
                        {b.is_draft && <button className="btn btn-sm btn-teal" onClick={() => loadDraft(b)}><Ic n="edit" s={15} />Düzenle</button>}
                        <button className="btn btn-sm btn-danger" onClick={() => del(b)}><Ic n="trash" s={15} />Sil</button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )
      )}
    </div>
  );
}
